import { useCallback, useMemo, KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { UseDepartmentCardArgs } from "@model/Department.model";

export const useDepartmentCard = ({
  department,
  onOpen,
}: UseDepartmentCardArgs) => {
  const navigate = useNavigate();

  const handleCardClick = useCallback(() => {
    onOpen?.(department.id);
  }, [onOpen, department.id]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (!onOpen) return;
      if (e.target !== e.currentTarget) return;

      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen(department.id);
      }
    },
    [onOpen, department.id]
  );

  const stop = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const headLabel = useMemo(() => {
    const first = department.headOfDepartmentFirstName ?? "";
    const last = department.headOfDepartmentLastName ?? "";
    const fullName = `${first} ${last}`.trim();

    if (fullName) return fullName;
    if (department.headOfDepartmentUserId)
      return department.headOfDepartmentUserId;
    return "—";
  }, [
    department.headOfDepartmentFirstName,
    department.headOfDepartmentLastName,
    department.headOfDepartmentUserId,
  ]);

  const handleHeadClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!department.headOfDepartmentUserId) return;
      navigate(`/users/${department.headOfDepartmentUserId}`);
    },
    [department.headOfDepartmentUserId, navigate]
  );

  return {
    handleCardClick,
    handleKeyDown,
    stop,
    headLabel,
    handleHeadClick,
  };
};
